import { clone } from 'lodash'
import Http from '@/assets/ts/classes/Http'
import type Exercise from '@/assets/ts/interfaces/Exercise'
import type ExerciseSwap from '@/assets/ts/interfaces/ExerciseSwap'
import PreviousSessionData from '@/assets/ts/interfaces/PreviousSessionData'
import type Session from '@/assets/ts/interfaces/Session'
import type SessionExercise from '@/assets/ts/interfaces/SessionExercise'
import type SessionSet from '@/assets/ts/interfaces/SessionSet'
import type SessionSetGroup from '@/assets/ts/interfaces/SessionSetGroup'
import type SessionStore from '@/assets/ts/interfaces/SessionStore'
import UserAddedSet from '@/assets/ts/interfaces/UserAddedSet'
import round from '@/assets/ts/utilities/round'

const useStoreSession = defineStore( 'sessionStore', () => {

  const previousSessionsStore = useStorePreviousSessions()
  const savedUserAddedSets = ref<UserAddedSet[]>( [] )
  const userDeletedSets = ref<UserAddedSet[]>( [] )

  const initialSession = {
    currentSetGroupIndex: 0,
    currentExerciseIndex: 0,
    currentSetIndex: 0,
    ID: null,
    inProgress: false,
    complete: false,
    completedAt: 0,
    programId: null,
    startedAt: 0,
    workoutDay: null,
    workoutWeekNumber: null,
    workoutTitle: null,
    workoutId: null,
    setGroups: [],
  }

  /**
   * The previous session that was completed using the current workout.
   */
  const previousSession = ref<Session|null>( null )
  const previousSessions = ref<Session[]>( [] )

  /**
   * Get the last session for todaysWorkout completed by the member.
   *
   * @param memberId The ID of the member.
   * @param workoutId The ID of the workout.
   * @param programId The ID of the program.
   * @returns The previous session if one exists.
   */
  const getPreviousSession = (
    memberId: number,
    workoutId: number,
    programId: number
  ) => Http.get( `/member/${memberId}/previous-session`, { params: { workoutId, programId } } )

  const getUserAddedSets = ( memberId: number, workoutId: number ) =>
    Http.get( `/member/${memberId}/user-added-sets`, { params: { workoutId } } )

  const session = useStorage<SessionStore>( 'session', clone( initialSession ) )

  // const previousSessionSet = computed( () => getPreviousSessionSet( set: SessionSet ) )

  const getPreviousSessionSet = ( set: SessionSet, previousSession: Session ) => {
    return previousSession.sets?.find( previousSet =>
      previousSet.setGroup === ( session.value.currentSetGroupIndex + 1 ) &&
      previousSet.exercise.title === set.exercise.title &&
      previousSet.setNumber === set.setNumber
    ) ?? null
  }

  const currentSetGroup = computed( () => session.value.inProgress ? session.value.setGroups[session.value.currentSetGroupIndex] : null )
  const currentExercise = computed( () => currentSetGroup.value?.exercises[session.value.currentExerciseIndex] )

  const previousSessionData = computed<Array<PreviousSessionData | null>>( () => {
    if ( ! currentExercise.value ) return []

    // Get only the most recent session (first in the array)
    const sessions = Array.from( previousSessionsStore.previousSessions )
    const lastSession = sessions.length > 0 ? sessions[0] : null

    if ( ! lastSession ) return currentExercise.value.sets.map( () => null )

    return currentExercise.value.sets.map( ( set ) => {
      const previousSet = getPreviousSessionSet( set, lastSession )

      // Only return data if the set exists and wasn't skipped
      if ( previousSet && ! previousSet.skipped ) {
        const data: PreviousSessionData = {
          weight: String( previousSet.weight ),
          reps: String( previousSet.reps ),
          rir: String( previousSet.rir ),
          units: lastSession.trainingUnits ? unitsAbbr( lastSession.trainingUnits ) : ''
        }
        // Include subset data if it exists
        if ( previousSet.subset ) {
          data.subset = {
            weight: String( previousSet.subset.weight ),
            reps: String( previousSet.subset.reps ),
            rir: String( previousSet.subset.rir )
          }
        }
        return data
      }

      return null
    } )
  } )

  const syncSession = async ( custom: any ) => {
    return Http.post( '/sync-session', custom )
  }

  /**
   * A session payload saved locally when syncing fails (offline or error).
   * Will be retried automatically when the user comes back online.
   */
  const pendingSessionRaw = localStorage.getItem( 'pendingSession' )
  const pendingSession = ref<any>( pendingSessionRaw ? JSON.parse( pendingSessionRaw ) : null )
  const isSyncingPendingSession = ref( false )

  /**
   * Save a session payload locally for later sync.
   */
  const savePendingSession = ( payload: any ) => {
    pendingSession.value = payload
    if ( payload ) {
      localStorage.setItem( 'pendingSession', JSON.stringify( payload ) )
    } else {
      localStorage.removeItem( 'pendingSession' )
    }
  }

  /**
   * Attempt to sync a pending session that was saved while offline.
   * Clears the pending session on success and refreshes the workout week.
   */
  const syncPendingSession = async () => {
    if ( ! pendingSession.value || isSyncingPendingSession.value ) return

    isSyncingPendingSession.value = true
    try {
      const response = await syncSession( pendingSession.value )
      if ( response?.updated ) {
        console.log( 'Pending session synced successfully.' )
        savePendingSession( null )

        // Refresh workout week so UI advances to the next workout.
        const profileStore = useStoreProfile()
        const workoutWeekStore = useStoreWorkoutWeek()
        if ( profileStore.profile?.ID ) {
          await workoutWeekStore.fetchWorkoutWeekFromDatabase( profileStore.profile.ID )
        }
      } else {
        console.warn( 'Pending session sync returned an error, will retry later.', response )
      }
    } catch ( error ) {
      console.error( 'Failed to sync pending session, will retry later.', error )
    } finally {
      isSyncingPendingSession.value = false
    }
  }

  /**
   * Watch for network recovery and automatically retry pending session sync.
   */
  const isOnline = useOnline()
  watch( isOnline, ( online ) => {
    if ( online && pendingSession.value ) {
      syncPendingSession()
    }
  } )

  /**
   * Resets the session to its default state.
   */
  const resetSession = () => session.value = clone( initialSession )

  const addSetToExercise = ( exercise: SessionExercise, exerciseIndex: number ) => {
    const profileStore = useStoreProfile()
    const isUnilateral = exercise.exerciseTypes?.some( type => type.slug === 'unilateral' )

    const newSet: SessionSet = {
      adAmount: 0,
      currentSet: 0,
      difficulty:	0,
      rir: null,
      rest: exercise.sets[0].rest,
      exercise: {
        ID: exercise.ID,
        title: exercise.title,
        switchedToMain: false,
        exerciseTypes: exercise.exerciseTypes,
      },
      exerciseNumber: exerciseIndex + 1,
      ghostReps: 0,
      ghostWeight: 0,
      notes: exercise.sets[0].notes,
      percentComplete: 0,
      reps: 0,
      suggestedReps: exercise.sets[0].suggestedReps,
      setGroup: session.value.currentSetGroupIndex + 1,
      setNotes: [],
      setNumber: exercise.sets.length + 1,
      setType: currentSetGroup.value ? currentSetGroup.value.exercises.length > 1 ? 'super_set' : 'straight_set' : '',
      completed: false,
      skipped: false,
      targetRir: exercise.sets[0].targetRir,
      suggestedWeight: 0,
      userAdded: true,
      weight: 0,
      unilateral_enabled: isUnilateral ? ( profileStore.profile.settings.unilateralTrackingEnabled ?? true ) : false,
    }

    // Add subset for unilateral exercises
    if ( isUnilateral ) {
      newSet.subset = {
        weight: 0,
        reps: 0,
        rir: null,
        side: 'R'
      }
      newSet.unilateral_enabled = profileStore.profile.settings.unilateralTrackingEnabled ?? true
    }

    session.value.setGroups[session.value.currentSetGroupIndex].exercises[exerciseIndex].sets.push( newSet )
  }

  const currentExerciseToSwap = ref<Exercise|SessionExercise|null>( null )

  const userAddedSets = computed( () => {
    return session.value.setGroups.flatMap( setGroup =>
      setGroup.exercises.flatMap( exercise =>
        exercise.sets.filter( set => set.userAdded )
      )
    )
  } )

  // const currentSetIndex = ref( 0 )

  const setGroup = computed( () => getSetGroup.value( session.value.currentSetGroupIndex ) )
  const hasMoreExercisesInSetGroup = computed( () => setGroup.value ? session.value.currentExerciseIndex < setGroup.value.exercises.length - 1 : false )
  const hasMoreSetsInSetGroup = computed( () => setGroup.value ? session.value.currentSetIndex < setGroup.value.exercises[session.value.currentExerciseIndex].sets.length - 1 : false )

  const swappedExercisesInSession = computed( () => allExercisesInSession.value.filter( exercise => ( exercise.ID !== exercise.defaultExerciseId ) || exercise.switchedToMain ) )

  /**
   * The computed exercise swaps prepped for the API.
   */
  const exerciseSwaps: ComputedRef<ExerciseSwap[]> = computed( () => swappedExercisesInSession.value.map( exercise => {
    return {
      save: true,
      title: exercise.title,
      defaultExerciseTitle: exercise.defaultExerciseTitle,
      programId: session.value.programId,
      workoutId: session.value.workoutId,
      setGroup: exercise.sets[0].setGroup,
      exerciseNumber: exercise.sets[0].exerciseNumber,
      alternateId: exercise.ID,
      delete: exercise.switchedToMain
    }
  } ) )

  const totalSetsInCurrentSetGroup = computed( () => {
    return currentSetGroup.value?.exercises.reduce( ( total, exercise ) => {
      return total + exercise.sets.length
    }, 0 )
  } )

  const totalSetsInCurrentExercise = computed( () => currentExercise.value?.sets.length )

  const totalExercisesInSession = computed( () => {
    return session.value.setGroups.reduce( ( total, setGroup ) => {
      return total + setGroup.exercises.length
    }, 0 )
  } )

  /**
   * Get a flattened map of all exercises in the session.
   */
  const allExercisesInSession = computed( () => {
    return session.value.setGroups.flatMap( setGroup => setGroup.exercises )
  } )

  const getCompletedExercisesInSession = computed( () => {
    return allExercisesInSession.value.filter( exercise =>
      exercise.sets.every( set => set.skipped || set.completed )
    ).length
  } )

  const getCompletedSetsInExercise = computed( () => {
    return ( setGroupIndex: number, exerciseIndex: number ) => {
      return session.value.setGroups[setGroupIndex].exercises[exerciseIndex].sets.reduce( ( total, set ) => {
        return total + ( set.completed || set.skipped ? 1 : 0 )
      }, 0 )
    }
  } )

  const currentExerciseInSession = computed( () => {
    return session.value.setGroups.reduce( ( total, setGroup, setGroupIndex ) => {
      return total + setGroup.exercises.reduce( ( exerciseTotal, exercise, exerciseIndex ) => {
        return exerciseTotal + ( setGroupIndex === session.value.currentSetGroupIndex && exerciseIndex === session.value.currentExerciseIndex ? 1 : 0 )
      }, 0 )
    }, 0 )
  } )

  /**
   * Returns the total number of sets across exercises in a set group.
   *
   * @param setGroupIndex The index of the set group.
   */
  const getTotalSetsInSetGroup = ( setGroupIndex: number ) => {
    return session.value.setGroups[setGroupIndex].exercises.reduce( ( exerciseTotal, exercise ) => {
      return exerciseTotal + exercise.sets.length
    }, 0 )
  }

  /**
   * Determine if a set group is completed by its index.
   */
  const getSetGroupIsCompleted = computed( () => {
    return ( setGroupIndex: number ) => {
      return getTotalSetsInSetGroup( setGroupIndex ) === getCompletedSetsInSetGroup.value( setGroupIndex )
    }
  } )

  const getSetGroupIsRemainingOrSkipped = computed( () => {
    return ( setGroupIndex: number ) => {
      return getOnlyCompletedSetsInSetGroup.value( setGroupIndex ) === 0 && getSkippedSetsInSetGroup.value( setGroupIndex ) > 0 && getSkippedSetsInSetGroup.value( setGroupIndex ) < getTotalSetsInSetGroup( setGroupIndex )
    }
  } )

  /**
   * Determine if a set group has not been started.
   */
  const getSetGroupIsRemaining = computed( () => {
    return ( setGroupIndex: number ) => {
      return getCompletedSetsInSetGroup.value( setGroupIndex ) === 0
    }
  } )

  const currentExerciseNotes = computed( () => {
    return currentSetGroup.value?.exercises[session.value.currentExerciseIndex]?.sets.flatMap( set => set.setNotes ) || []
  } )

  /**
   * Determine if a set group has been skipped.
   */
  const getSetGroupIsSkipped = computed( () => {
    return ( setGroupIndex: number ) => {
      return getTotalSetsInSetGroup( setGroupIndex ) === getSkippedSetsInSetGroup.value( setGroupIndex )
    }
  } )

  // const getCompletedSetsInSetGroup = computed( () => {
  //   return ( setGroupIndex: number ) => {
  //     return session.value.setGroups[setGroupIndex].exercises.reduce( ( exerciseTotal, exercise ) => {
  //       return exerciseTotal + exercise.sets.reduce( ( setTotal, set ) => {
  //         return setTotal + ( set.completed || set.skipped ? 1 : 0 )
  //       }, 0 )
  //     }, 0 )
  //   }
  // }

  /**
   * Returns the total number of completed/skipped sets in the current set group.
   */
  const completedSetsInCurrentSetGroup = computed( () => {
    return currentSetGroup.value?.exercises.reduce( ( total, exercise ) => {
      return total + exercise.sets.reduce( ( setTotal, set ) => {
        return setTotal + ( set.completed || set.skipped ? 1 : 0 )
      }, 0 )
    }, 0 )
  } )

  const totalSetsInSession = computed( () => {
    return session.value.setGroups?.reduce( ( total, setGroup ) => {
      return total + setGroup.exercises.reduce( ( exerciseTotal, exercise ) => {
        return exerciseTotal + exercise.sets.length
      }, 0 )
    }, 0 )
  } )

  const completedSetsInSession = computed( () => {
    return session.value.setGroups?.reduce( ( total, setGroup ) => {
      return total + setGroup.exercises.reduce( ( exerciseTotal, exercise ) => {
        return exerciseTotal + exercise.sets.reduce( ( setTotal, set ) => {
          return setTotal + ( set.completed || set.skipped ? 1 : 0 )
        }, 0 )
      }, 0 )
    }, 0 )
  } )

  // const completedSets = computed( () => {
  //   return session.value.setGroups.reduce( ( acc, setGroup ) => {
  //     return acc + setGroup.completedSets
  //   }, 0 )
  // } )

  const getSetGroup = computed( () => {
    return ( setGroupIndex: number ) => {
      return session.value.setGroups[setGroupIndex]
    }
  } )

  /**
   * Returns the number of completed/skipped sets in a set group.
   *
   * @param setGroupIndex The index of the set group.
   */
  const getCompletedSetsInSetGroup = computed( () => {
    return ( setGroupIndex: number ) => {
      return session.value.setGroups[setGroupIndex].exercises.reduce( ( exerciseTotal, exercise ) => {
        return exerciseTotal + exercise.sets.reduce( ( setTotal, set ) => {
          return setTotal + ( set.completed || set.skipped ? 1 : 0 )
        }, 0 )
      }, 0 )
    }
  } )

  const getOnlyCompletedSetsInSetGroup = computed( () => {
    return ( setGroupIndex: number ) => {
      return session.value.setGroups[setGroupIndex].exercises.reduce( ( exerciseTotal, exercise ) => {
        return exerciseTotal + exercise.sets.reduce( ( setTotal, set ) => {
          return setTotal + ( set.completed ? 1 : 0 )
        }, 0 )
      }, 0 )
    }
  } )

  const getSkippedSetsInSetGroup = computed( () => {
    return ( setGroupIndex: number ) => {
      return session.value.setGroups[setGroupIndex].exercises.reduce( ( exerciseTotal, exercise ) => {
        return exerciseTotal + exercise.sets.reduce( ( setTotal, set ) => {
          return setTotal + ( set.skipped ? 1 : 0 )
        }, 0 )
      }, 0 )
    }
  } )

  const percentComplete = computed( () => session.value.setGroups.length ? round( completedSetsInSession.value / totalSetsInSession.value * 100, 0 ) : 0 )

  /**
   * Flatten all set groups and exercises into a set list.
   */
  const allSetGroupsAsExercises = computed( () =>
    session.value.setGroups.flatMap( ( setGroup, setGroupIndex ) =>
      setGroup.exercises.flatMap( exercise =>
        exercise.sets
      )
    )
  )

  const firstIncompleteSet = computed( () => allSetGroupsAsExercises.value.find( set => ! set.completed && ! set.skipped ) )
  const getExerciseWithLeastCompletedSets = computed( () => {
    return ( setGroup: SessionSetGroup ) => {
      let minCompletedSets = Infinity
      let exerciseWithLeastCompletedSets = 0

      setGroup.exercises.forEach( ( exercise, exerciseIndex ) => {
        const completedSetsCount = exercise.sets.filter( set => set.completed ).length
        if ( completedSetsCount < minCompletedSets ) {
          minCompletedSets = completedSetsCount
          exerciseWithLeastCompletedSets = exerciseIndex
        }
      } )

      return exerciseWithLeastCompletedSets
    }
  } )

  /**
   * Get the next exercise to display in the modal title.
   */
  const upNext = computed( () => {
    if ( currentSetGroup.value && ! getSetGroupIsCompleted.value( session.value.currentSetGroupIndex ) ) {
      return currentSetGroup.value.exercises[session.value.currentExerciseIndex]?.sets[session.value.currentSetIndex]?.exercise.title
    } else if ( firstIncompleteSet.value ) {
      /**
       * TODO: Return the second set of a superset if set one was completed.
       *
       * This currently returns the first incomplete set, which will be the second
       * set of a super set exercise if one exercise has been completed.
       */
      return firstIncompleteSet.value.exercise.title
    } else {
      return 'Finished!'
    }
  } )

  return {
    addSetToExercise,
    allExercisesInSession,
    session,
    currentExercise,
    currentExerciseNotes,
    resetSession,
    exerciseSwaps,
    firstIncompleteSet,
    getCompletedExercisesInSession,
    getSetGroup,
    getSetGroupIsCompleted,
    getSetGroupIsRemaining,
    getSetGroupIsSkipped,
    getCompletedSetsInExercise,
    getCompletedSetsInSetGroup,
    getOnlyCompletedSetsInSetGroup,
    getSetGroupIsRemainingOrSkipped,
    getExerciseWithLeastCompletedSets,
    getPreviousSession,
    previousSessions,
    hasMoreExercisesInSetGroup,
    hasMoreSetsInSetGroup,
    previousSession,
    previousSessionData,
    percentComplete,
    completedSetsInSession,
    currentExerciseToSwap,
    currentSetGroup,
    // nextExercise,
    // nextSet,
    syncSession,
    savePendingSession,
    syncPendingSession,
    pendingSession,
    swappedExercisesInSession,
    totalExercisesInSession,
    totalSetsInSession,
    totalSetsInCurrentSetGroup,
    totalSetsInCurrentExercise,
    completedSetsInCurrentSetGroup,
    allSetGroupsAsExercises,
    upNext,
    savedUserAddedSets,
    userAddedSets,
    userDeletedSets,
    getUserAddedSets,
  }
} )

export default useStoreSession